// CRM C-I/B-II: HTML in Signaturen — und die drei Umrechnungen, die dafür nötig sind.
//
// Eine Outlook-Signatur ist HTML: Tabelle, Farben, Logo, Link. Sie als Text zu speichern
// heißt, sie zu verlieren. Deshalb ist der Signatur-Körper HTML — und deshalb braucht es
// hier eine Grenze, hinter der fremdes HTML nicht mehr gefährlich ist: eine **geteilte**
// Vorlage kann von einem anderen Nutzer stammen (`adminGroups` am Datensatz), und ihr
// Inhalt landet in unserem Fenster im DOM. Ungeprüft wäre das eine XSS-Lücke im
// Dashboard, nicht bloß eine hässliche Mail.
//
// `dompurify` liegt bereits im Baum (jspdf zieht es). Hier steht es als **direkte**
// Abhängigkeit in der package.json, damit ein jspdf-Update es nicht unter uns wegzieht.
import DOMPurify from "dompurify";

// Was in einer Signatur vorkommt — und nichts darüber hinaus. `style` ist erlaubt, weil
// Outlook alles darüber macht; `script`, `iframe`, `form` und Event-Attribute wirft
// DOMPurify ohnehin weg, sie stehen hier nur zur Deutlichkeit.
const SIGNATURE_CONFIG = {
  ALLOWED_TAGS: [
    "a", "b", "strong", "i", "em", "u", "s", "br", "p", "div", "span", "font",
    "table", "thead", "tbody", "tr", "td", "th", "ul", "ol", "li", "img", "hr",
    "small", "sup", "sub", "h1", "h2", "h3", "h4", "h5", "h6", "pre", "code", "blockquote",
  ],
  ALLOWED_ATTR: [
    "href", "src", "alt", "title", "width", "height", "style", "color", "face",
    "size", "align", "valign", "colspan", "rowspan", "target", "rel", "border",
    "cellpadding", "cellspacing", "bgcolor", "dir",
  ],
  // Ein Bild darf aus dem Netz oder eingebettet kommen. `file:///…` und `cid:` NICHT —
  // siehe die Warnung im Editor: Outlook hängt sein Logo als `cid:` an die Mail, und
  // beim Kopieren in die Zwischenablage bleibt ein Pfad auf die eigene Festplatte
  // stehen. Der wäre beim Empfänger ein leerer Rahmen. Lieber wegwerfen als so senden.
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|data:image\/(?:png|jpe?g|gif|webp|bmp|svg\+xml|x-icon|tiff|pjpeg|x-png)(?:;[a-zA-Z0-9=+ -]+)*;base64,)/i,
  // DIE FALLE AN `ALLOWED_URI_REGEXP`: DOMPurify prueft damit nicht nur `href` und `src`,
  // sondern den WERT JEDES Attributs, das nicht als URI-sicher gilt — und seine Liste
  // dafuer ist kurz (`alt`, `title`, `style`, `id`, `class`, …). `width="140"` faellt
  // durch den Ausdruck oben und wird STILL entfernt. Ein Logo, das Outlook ueber die
  // Attribute auf 140x26 zieht, kam beim Empfaenger dann in seiner vollen Groesse an.
  // Keines der Attribute hier traegt eine Adresse; `href`, `src` und `background`
  // stehen bewusst NICHT darin und werden weiter geprueft.
  ADD_URI_SAFE_ATTR: [
    "width", "height", "border", "cellpadding", "cellspacing", "colspan", "rowspan",
    "align", "valign", "bgcolor", "color", "face", "size", "dir", "target", "rel",
  ],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "link", "meta"],
  FORBID_ATTR: ["srcset", "formaction", "background"],
};

/** Fremdes oder eingefügtes HTML auf das reduzieren, was eine Signatur braucht. */
export const sanitizeHtml = (html) =>
  DOMPurify.sanitize(String(html ?? ""), SIGNATURE_CONFIG);

// Bewusst NICHT die Kurzform `font`: `removeProperty("font")` loescht auch
// `font-weight`/`font-style` — Fett und Kursiv waeren weg.
const FONT_PROPERTIES = ["font-family", "font-size", "line-height"];

/**
 * Schriftart und -groesse aus fremdem HTML entfernen, damit eingefuegter Text die
 * Schrift des Textkoerpers uebernimmt (#4272). Aus einer Webseite oder Word kopiert,
 * bringt jeder Absatz sonst seine eigene `font-family`/`font-size` mit, und die Mail
 * wechselt mitten im Satz die Schrift. Fett, Kursiv, Links und Farben bleiben.
 * Unicode-"Schrift"-Buchstaben werden dabei mit `plainLetters` zu normalen.
 */
// "𝑽𝒊𝒏𝒕𝒂𝒈𝒆" ist keine Schrift, sondern eigene Unicode-Zeichen (Mathematical
// Alphanumeric Symbols, dazu das kursive ℎ aus Letterlike Symbols) — Social-Media-Texte
// "formatieren" so. `normalize("NFKC")` macht daraus normale Buchstaben, aber nur fuer
// diesen Block: auf den ganzen Text angewandt wuerde es auch "²" zu "2" und "…" zu "..."
// machen.
const STYLED_LETTERS = /[\u{1D400}-\u{1D7FF}\u{210E}]/gu;

/** Unicode-"Schrift"-Buchstaben (𝑽𝒊𝒏𝒕𝒂𝒈𝒆) in normale Buchstaben umwandeln. */
export const plainLetters = (text) =>
  String(text ?? "").replace(STYLED_LETTERS, (char) => char.normalize("NFKC"));

export const stripFonts = (html) => {
  const doc = new DOMParser().parseFromString(plainLetters(html), "text/html");
  for (const element of doc.body.querySelectorAll("*")) {
    if (element.hasAttribute("style")) {
      FONT_PROPERTIES.forEach((property) => element.style.removeProperty(property));
      if (!element.getAttribute("style").trim()) element.removeAttribute("style");
    }
    if (element.tagName === "FONT") {
      element.removeAttribute("face");
      element.removeAttribute("size");
    }
  }
  return doc.body.innerHTML;
};

// At-Regeln (`@font-face`, `@page`, `@media`) haben keinen Empfaenger im Dokument —
// sie fliegen raus, bevor der Rest gelesen wird. Eine Ebene Verschachtelung reicht fuer
// alles, was Word schreibt.
const AT_RULE = /@[a-z-]+[^{]*\{(?:[^{}]|\{[^{}]*\})*\}/gi;
const CSS_RULE = /([^{}]+)\{([^{}]*)\}/g;

// `mso-ligatures`, `behavior:url(#default#VML)`, `page:WordSection1` — Word-interne
// Angaben, die kein Browser und kein Mailprogramm liest. Sie wuerden die Signatur nur
// um ein Vielfaches aufblaehen, und `body` hat ein 300-KB-Limit.
const isUseful = (declaration) =>
  declaration && !/^(mso-|behavior\s*:|page\s*:|tab-interval\s*:)/i.test(declaration);

const usefulDeclarations = (block) =>
  block
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(isUseful)
    .join(";");

/**
 * Den `<style>`-Block einer Outlook-Signatur in die Elemente schreiben.
 *
 * Word legt das Aussehen NICHT nur in `style`-Attribute: `p.MsoNormal {margin:0cm;
 * font-size:12.0pt; font-family:"Aptos"}` steht im Kopf der Datei. Ohne diese Regeln
 * bekommt jeder Absatz den Browser-Standardabstand von einer Zeile und eine andere
 * Schrift — die Signatur saehe sichtbar anders aus als die aus Outlook gesendete, und
 * genau das ist die Anforderung.
 *
 * Den Block mitzunehmen geht nicht: im Fenster waere er globales CSS unserer Seite
 * (`p {…}` faerbt dann das Dashboard um), und Gmail wirft `<style>` beim Empfaenger
 * ohnehin weg. Inline-Styles ueberleben ueberall.
 *
 * ponytail: Regex statt CSSOM. Word-CSS ist eine flache Liste `sel { decls }`.
 * Ceiling: ein `;` in einem Anführungszeichen oder eine verschachtelte At-Regel wird
 * falsch gelesen — dann ist `new CSSStyleSheet()` + `cssRules` faellig.
 */
export const inlineCss = (html) => {
  const source = String(html ?? "");
  if (!/<style/i.test(source)) return source;
  const doc = new DOMParser().parseFromString(source, "text/html");
  const styles = [...doc.querySelectorAll("style")];
  if (!styles.length) return source;

  // Reihenfolge zaehlt: eine spaetere Regel schlaegt eine fruehere, und das
  // `style`-Attribut am Element schlaegt beide. Deshalb wird pro Element gesammelt und
  // der urspruengliche Inline-Wert ZULETZT angehaengt.
  const collected = new Map();
  const inline = new Map();

  for (const style of styles) {
    const css = String(style.textContent || "")
      // Word verpackt sein CSS in einen HTML-Kommentar.
      .replace(/<!--|-->/g, "")
      .replace(/\/\*[\s\S]*?\*\//g, "")
      .replace(AT_RULE, "");
    let match;
    CSS_RULE.lastIndex = 0;
    while ((match = CSS_RULE.exec(css))) {
      const declarations = usefulDeclarations(match[2]);
      if (!declarations) continue;
      for (const part of match[1].split(",")) {
        const selector = part.trim();
        // `v\:* {behavior:…}` — VML-Platzhalter, kein gueltiger Selektor und ohne Wert.
        if (!selector || selector.includes("\\")) continue;
        let targets = [];
        try {
          targets = [...doc.querySelectorAll(selector)];
        } catch (error) {
          continue;
        }
        for (const element of targets) {
          if (!inline.has(element)) inline.set(element, element.getAttribute("style") || "");
          collected.set(element, `${collected.get(element) || ""}${declarations};`);
        }
      }
    }
    style.remove();
  }

  for (const [element, declarations] of collected) {
    element.setAttribute("style", `${declarations}${inline.get(element)}`);
  }
  return doc.body.innerHTML;
};

const escapeHtml = (text) =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Der getippte Text wird HTML: escapen, Umbrüche zu `<br>`.
 *
 * ponytail: kein Rich-Text im Textkörper. Der Text bleibt Text, nur die Signatur ist
 * HTML — genau das war gefordert. Ceiling: wer Fett und Listen IM TEXT braucht, tauscht
 * das Textfeld gegen einen Editor; `plainToHtml` fällt dann weg, alles andere bleibt.
 */
export const plainToHtml = (text) =>
  escapeHtml(text).replace(/\r\n|\r|\n/g, "<br>");

/**
 * HTML zurück zu lesbarem Text — für `Emails.bodyText`.
 *
 * **Warum das hier passiert und nicht im Lambda:** `bodyText` heißt Text. Der Sendepfad
 * schrieb bisher `bodyText: input.body`, und mit HTML-Körper hätte dort rohes HTML in
 * einem Feld namens „Text" gelegen — genau die Falle, die der Plan beim Sync-Lambda
 * benennt. Wir kennen den Klartext bereits, also schicken wir ihn mit, statt HTML
 * hinterher zu zerlegen.
 */
/**
 * Getippt wird „lineup.de", nicht „https://lineup.de". Ohne Schema waere der Link im
 * Postfach des Empfaengers relativ und damit tot — deshalb kommt es davor, wenn der
 * Nutzer keines gesetzt hat.
 *
 * Eine Mailadresse bekommt `mailto:`: `https://info@lineup.de` waere sonst der
 * haeufigste Fehler, den dieses Feld erzeugen kann.
 */
export const withScheme = (url) => {
  const value = String(url ?? "").trim();
  if (!value || /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("//")) {
    return value;
  }
  if (/^[^/\s@]+@[^/\s@]+$/.test(value)) return `mailto:${value}`;
  return `https://${value}`;
};

export const htmlToPlain = (html) =>
  String(html ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    // Absatz und Überschrift trennen mit Leerzeile, ein `div` ist in HTML-Mail nur der
    // Zeilenumbruch — sonst reißt eine Signatur aus lauter `div` den Text auseinander.
    .replace(/<\/(p|h[1-4])>/gi, "\n\n")
    .replace(/<\/(div|tr|li|blockquote)>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, "&")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
